import { jwtDecode } from 'jwt-decode';
import { get } from 'lodash';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Urls } from '@imaginelearning/web-api-core';
import { ApiProduct } from '../constants/apiProducts';
import { ClientId, Environment as Environments, toEnvironment } from '@imaginelearning/web-auth';
import { ClientDetector, Clients } from './client.detector';
import { Client } from '../app.constants';
import { FeatureFlagEnum } from './featureflags.model';
import { Products } from '../constants/products';
import { ProductDisabledReasons } from '../constants/productDisabledReasons';
import * as i0 from "@angular/core";
import * as i1 from "@imaginelearning/web-api-core";
import * as i2 from "@angular/common/http";
export var LoginType;
(function (LoginType) {
    LoginType["CleverLoginFlow"] = "CleverLoginFlow";
    LoginType["SSOOAuthFlow"] = "SSOOAuthFlow";
    LoginType["LtiFlow"] = "LtiFlow";
})(LoginType || (LoginType = {}));
export class SSOHelpers {
    constructor(environment, http) {
        this.environment = environment;
        this.http = http;
        this.authParam = 'useIlAuth'; // All SSO goes through IL Auth for now so we need to tell the product selection page to use it
        this.urls = Urls.get(this.environment.config.ENV);
    }
    SSOLogin(providerRootUrl, encryptedToken, signature = '', client_id, ssoProvidedProduct) {
        if (!encryptedToken || encryptedToken?.length === 0) {
            return of({
                data: {
                    status: 400,
                    error: 'Bad Request',
                    message: 'EncryptedToken is missing'
                }
            });
        }
        let params = `grant_type=ssotoken&client_id=${client_id ??
            ClientId.Portal}&encryptedToken=${encryptedToken}&signature=${signature}`;
        if (ssoProvidedProduct) {
            params += `&ssoProvidedProduct=${ssoProvidedProduct}`;
        }
        return this.http.post(`${providerRootUrl}/connect/Token`, params, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            withCredentials: true
        });
    }
    authenticateOauthRequest(code, realmName, redirectUri) {
        if (!code || code?.length === 0 || !realmName || realmName?.length === 0) {
            return of({
                data: {
                    status: 400,
                    error: 'Bad Request',
                    message: `Code : ${code}  RealmName: ${realmName}`
                }
            });
        }
        return this.http.get(`${this.urls.singleSignOnApiUrl}/singlesignon/${realmName}/${code}/AuthenicateOauthRequest?redirectUri=${encodeURIComponent(redirectUri)}`);
    }
    SsoCookieLogin(loginType, loginFlowDetails) {
        switch (loginType) {
            case LoginType.CleverLoginFlow:
                return this.SsoCleverLogin(loginFlowDetails.Token, loginFlowDetails.Signature, loginFlowDetails.Role, loginFlowDetails.Product);
            case LoginType.SSOOAuthFlow:
                return this.SsoOAuthLogin(loginFlowDetails.Token, loginFlowDetails.Product);
        }
    }
    SsoOAuthLogin(token, ssoProvidedProduct) {
        if (!token || token?.length === 0) {
            return of({
                data: {
                    status: 400,
                    error: 'Bad Request',
                    message: 'invalid_token'
                }
            });
        }
        var myForm = document.createElement('form');
        myForm.setAttribute('action', this.environment.config.oauthSsoUrl);
        myForm.setAttribute('method', 'post');
        myForm.setAttribute('hidden', 'true');
        myForm.appendChild(this.createFormElement('token', token));
        myForm.appendChild(this.createFormElement('client_id', Client.WebSsoClient));
        myForm.appendChild(this.createFormElement('redirect_uri', this.environment.config.pssUrl));
        myForm.appendChild(this.createFormElement('sso_provided_product', ssoProvidedProduct));
        document.body.appendChild(myForm);
        myForm.submit();
        return EMPTY;
    }
    SsoCleverLogin(token, signature, role, ssoProvidedProduct) {
        if (!token || token?.length === 0) {
            return of({
                data: {
                    status: 400,
                    error: 'Bad Request',
                    message: 'invalid_token'
                }
            });
        }
        var myForm = document.createElement('form');
        myForm.setAttribute('action', this.environment.config.cleverSsoUrl);
        myForm.setAttribute('method', 'post');
        myForm.setAttribute('hidden', 'true');
        myForm.appendChild(this.createFormElement('token', token));
        myForm.appendChild(this.createFormElement('signature', signature));
        myForm.appendChild(this.createFormElement('client_id', Client.WebSsoClient));
        myForm.appendChild(this.createFormElement('redirect_uri', this.environment.config.pssUrl));
        myForm.appendChild(this.createFormElement('sso_provided_product', ssoProvidedProduct));
        document.body.appendChild(myForm);
        myForm.submit();
        return EMPTY;
    }
    getIntendedProduct(jwt) {
        const decodedToken = jwtDecode(jwt) || {};
        return get(decodedToken, 'http://il/requestedProduct', null);
    }
    getRoleFromToken(jwt) {
        const decodedToken = jwtDecode(jwt) || {};
        return get(decodedToken, 'role', null);
    }
    getUserIdFromToken(jwt) {
        const decodedToken = jwtDecode(jwt) || {};
        return get(decodedToken, 'http://il/id', null);
    }
    getStudentLaunchUrl(ssoDetails, loginFlowDetails, featureFlagService, permissionsService) {
        const product = this.getIntendedProduct(ssoDetails.EncryptedToken);
        const client = ClientDetector.detectClient();
        return this.redirectToProductPortal(product, client, ssoDetails, featureFlagService, permissionsService).pipe(switchMap(redirectResult => {
            if (redirectResult.redirectToProductPortal) {
                if (redirectResult.needsEncryptedToken) {
                    //If we need to redirect to product portal and need an encrypted token, then we need to inject it into
                    //the native device
                    return this.SsoCookieLogin(ssoDetails.LoginType, loginFlowDetails).pipe(switchMap(() => {
                        return of(`${this.environment.config.pssUrl}?${product ? 'launch=' + product : ''}`);
                    }), catchError(error => {
                        console.error(error);
                        return of(`${this.environment.config.pssUrl}?${product ? 'launch=' + product : ''}`);
                    }));
                }
                else {
                    return of(`${this.environment.config.pssUrl}?${product ? 'launch=' + product : ''}`);
                }
            }
            else {
                return this.getPreferredClient(ssoDetails.EncryptedToken, ssoDetails.SiteCode).pipe(map(client => {
                    return this.getNativeLaunchUrl(ssoDetails, client);
                }), catchError(error => {
                    console.error(error);
                    return this.getNativeLaunchUrl(ssoDetails, client);
                }));
            }
        }), catchError(error => {
            console.error(error);
            return of(`${this.environment.config.pssUrl}?${product ? 'launch=' + product : ''}`);
        }));
    }
    redirectToProductPortal(product, playerClient, ssoDetails, featureFlagService, permissionsService) {
        let redirectResult = {
            redirectToProductPortal: false,
            needsEncryptedToken: false
        };
        if (ssoDetails.CheckBenchmark &&
            (!product || product === ApiProduct.ILE || product === ApiProduct.Spanish) &&
            playerClient !== Clients.Html5) {
            //If product is spanish and user is using a native client, check if we need to redirect to product portal
            if (product === ApiProduct.Spanish) {
                //Evalaute flag
                return featureFlagService
                    .evaluateFlag(FeatureFlagEnum.RedirectSpanishWhenPendingAssessment, {
                    userId: ssoDetails.UserId,
                    role: ssoDetails.Role,
                    product: product
                })
                    .pipe(switchMap((flagEvaluationResult) => {
                    //If flag is on
                    if (flagEvaluationResult) {
                        return permissionsService.getProductPermissions(ssoDetails.UserId, ssoDetails.EncryptedToken).pipe(switchMap(response => {
                            //And permissions service says assessment is locked out for spanish, then redirect to product portal
                            //and don't launch native
                            if (response.products.find(p => p.product === Products.Spanish &&
                                p.assessmentLockout &&
                                p.disabledReason === ProductDisabledReasons.PendingAssessmentLockout)) {
                                redirectResult.redirectToProductPortal = true;
                                redirectResult.needsEncryptedToken = true;
                                return of(redirectResult);
                            }
                            return of(redirectResult);
                        }), catchError(error => {
                            console.error(error);
                            return of(redirectResult);
                        }));
                    }
                    return of(redirectResult);
                }), catchError(error => {
                    console.error(error);
                    return of(redirectResult);
                }));
            }
        }
        else {
            redirectResult.redirectToProductPortal = true;
        }
        return of(redirectResult);
    }
    getNativeLaunchUrl(ssoDetails, client, platform) {
        const clientPlatform = platform ?? ClientDetector.getInfo()?.os?.name;
        let prefix = !!clientPlatform?.match?.(/android/i) ? '' : 'il_';
        let query = [];
        let siteCode = ssoDetails?.SiteCode;
        const env = toEnvironment(this.environment.config.ENV);
        if (env === Environments.Development || env === Environments.Test) {
            siteCode += '@test';
        }
        else if (env === Environments.Rc) {
            siteCode += '@rc';
        }
        query.push(prefix + 'responseDetails=' + encodeURIComponent(ssoDetails.EncryptedToken));
        query.push(prefix + 'signature=' + encodeURIComponent(ssoDetails.Signature));
        query.push(prefix + 'role=' + encodeURIComponent(ssoDetails.Role));
        query.push(prefix + 'sitecode=' + encodeURIComponent(siteCode));
        query.push(prefix + 'enablehttps=true');
        if (client) {
            query.push(prefix + 'client=' + encodeURIComponent(client));
        }
        return this.environment.config.unityLaunchUrl.replace(/\/$/, '') + '/?' + query.join('&');
    }
    getPreferredClient(encryptedToken, sitecode) {
        return this.getPortalSettings(sitecode, encryptedToken).pipe(map((settings) => {
            if (settings && settings.hasOwnProperty('useNewHtml5Client')) {
                return settings.useNewHtml5Client ? 'new' : 'legacy';
            }
            return undefined;
        }));
    }
    getPortalSettings(sitecode, token) {
        let url = this.urls.managementApiUrl + '/portalsettings';
        if (sitecode) {
            url += '?sitecode=' + encodeURIComponent(sitecode);
        }
        let headers = token ? { headers: { Authorization: `bearer ${token}` } } : undefined;
        return this.http.get(url, headers).pipe(map((response) => {
            return response?.enabledDevelopmentFeatures || {};
        }));
    }
    createFormElement(name, value) {
        var myInput = document.createElement('input');
        myInput.setAttribute('type', 'text');
        myInput.setAttribute('name', name);
        myInput.setAttribute('value', value);
        return myInput;
    }
    static { this.ɵfac = function SSOHelpers_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || SSOHelpers)(i0.ɵɵinject(i1.Environment), i0.ɵɵinject(i2.HttpClient)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SSOHelpers, factory: SSOHelpers.ɵfac, providedIn: 'root' }); }
}
